import { Injectable } from '@angular/core';

import { AuthenticationToken } from '../entities/authentication-token.entity';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly refreshField = 'auth-refresh-token';
  private readonly tokenField = 'auth-access-token';

  private _refreshToken: AuthenticationToken = null;
  private _accessToken: AuthenticationToken = null;

  constructor() {
    this.loadTokenFromStorage();
  }

  /**
   * Checks access token availability
   * @returns true if access token exists, otherwise false
   */
  public hasToken(): boolean {
    return this._accessToken !== null;
  }

  /**
   * Checks refersh token availability
   * @returns true if refresh token exists, otherwise false
   */
  public hasRefreshToken(): boolean {
    return this._refreshToken !== null;
  }

  /**
   * Gets access token for bearer authentication
   * @returns access token
   */
  public getBearerToken(): string {
    return this._accessToken.token;
  }

  /**
   * Gets refresh token to retrieve a new access token
   * @returns refresh token
   */
  public getRefreshToken(): string {
    return this._refreshToken.token;
  }

  /**
   * Checks access token is expired
   * @returns true if access token is expired, otherwise false
   */
  public isAccessTokenExpired(): boolean {
    return this._accessToken.isExpired();
  }

  /**
   * Saves tokens to local storage
   * @param refreshToken
   * @param accessToken
   */
  public save(
    refreshToken: AuthenticationToken,
    accessToken: AuthenticationToken
  ): void {
    this._refreshToken = refreshToken;
    this._accessToken = accessToken;
    this.saveTokenToStorage();
  }

  /**
   * Updates local storage access token
   * @param accessToken
   */
  public updateAccessToken(accessToken: AuthenticationToken): void {
    this._accessToken = accessToken;
    this.saveTokenToStorage();
  }

  /**
   * Clears tokens
   */
  public clear(): void {
    this._refreshToken = null;
    this._accessToken = null;
    this.clearStorage();
  }

  /**
   * Loads tokens from local storage
   */
  private loadTokenFromStorage() {
    let refreshTokenData = localStorage.getItem(this.refreshField);
    let accessTokenData = localStorage.getItem(this.tokenField);

    if (refreshTokenData && accessTokenData) {
      this._refreshToken = new AuthenticationToken().deserialize(
        JSON.parse(refreshTokenData)
      );
      this._accessToken = new AuthenticationToken().deserialize(
        JSON.parse(accessTokenData)
      );
    }
  }

  /**
   * Saves current tokens to local storage
   */
  private saveTokenToStorage(): void {
    this.clearStorage();
    localStorage.setItem(this.refreshField, JSON.stringify(this._refreshToken));
    localStorage.setItem(this.tokenField, JSON.stringify(this._accessToken));
  }

  /**
   * Clears local storage only
   */
  private clearStorage(): void {
    localStorage.removeItem(this.refreshField);
    localStorage.removeItem(this.tokenField);
  }
}
